import { CATEGORY_EXPORT_LABELS } from './categories';
import { centsToDecimal, escapeText, quote } from './csv-format';
import type { Receipt, ReceiptSession } from './types';

/**
 * Accounting "bill import" CSV — the column set QuickBooks / Xero-style
 * importers expect, one row per line item with the bill-level fields
 * (supplier, dates, currency) repeated on every row of the same bill.
 *
 * Distinct from `export-csv.ts`, which is the analysis-friendly export:
 * that one carries Subtotal / Tax / Tip / Qty / Unit Price / Tags, none
 * of which this template has a column for. Both ship; users pick.
 *
 * Column names must keep their literal `*` prefixes — that is how the
 * importer recognises its mandatory columns, not decoration we can trim.
 *
 * Three mandatory columns have no direct source in a receipt:
 *   - `*DueDate` — a receipt is paid at the point of sale, so the due
 *     date IS the bill date. Inventing payment terms would be fiction.
 *   - `*Account` — filled from the receipt's category (our label set
 *     already includes `Utilities`, `Office`, … which read as chart-of-
 *     account names). Blank when the receipt was never categorised.
 *   - `*LineTaxCode` — see `taxCodeFor` below.
 */

const HEADERS = [
  '*BillNo',
  '*Supplier',
  '*BillDate',
  '*DueDate',
  'Terms',
  'Location',
  'Memo',
  '*Account',
  'LineDescription',
  '*LineAmount',
  '*LineTaxCode',
  'LineTaxAmount',
  'Currency',
] as const;

/**
 * VAT rates in force per country, keyed by the receipt's OCR language —
 * which is the closest thing to a country signal we hold, and is now
 * recorded per session (`ReceiptSession.lang`). Each list is that
 * country's standard rate plus its reduced rates.
 *
 * Language is a proxy, not a guarantee: `deu` covers Austria (20/13/10)
 * as well as Germany, and `eng` covers the US, where sales tax isn't VAT
 * and lands on arbitrary rates. Both cases fall through to the measured
 * percentage below rather than being snapped to a wrong rate, which is
 * the intended behaviour — see `taxCodeFor`.
 */
const VAT_RATES_BY_LANG: Record<string, readonly number[]> = {
  eng: [20, 5],                 // UK
  spa: [21, 10, 4],             // Spain
  fra: [20, 10, 5.5, 2.1],      // France
  deu: [19, 7],                 // Germany
  nld: [21, 9],                 // Netherlands
  ita: [22, 10, 5, 4],          // Italy
  por: [23, 13, 6],             // Portugal
};

/**
 * Union of every rate above, used when the receipt's language is unknown
 * (legacy sessions persisted before the per-session language field).
 * Wider net, so more chance of snapping to a rate the receipt's country
 * doesn't actually levy — hence only the fallback.
 */
const ALL_VAT_RATES = [
  ...new Set(Object.values(VAT_RATES_BY_LANG).flat()),
].sort((a, b) => a - b);

/** Snap-to tolerance in percentage points. */
const RATE_TOLERANCE = 0.6;

/**
 * DD/MM/YYYY — the format the template's own sample uses. Confirmed from
 * its data rather than assumed: `12/08/2026` + `Net 15` = `27/08/2026`,
 * which only holds if the day comes first.
 *
 * LOCAL-calendar based, matching the review UI: the parser creates
 * receipt dates at local midnight, so UTC getters would export the
 * previous day for every user east of Greenwich.
 */
function billDate(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

/**
 * Derive the VAT code for a receipt from `tax ÷ subtotal`, snapped to a
 * rate the receipt's own country actually levies.
 *
 * We hold ONE tax figure per receipt, not per line, so every line of a
 * bill gets the same code. That is exact for the common case (a receipt
 * taxed at a single rate) and a blended approximation for mixed baskets
 * — a Spanish grocery run mixing 21% and 4% items measures as neither,
 * so it stays unsnapped and exports the measured figure. The template's
 * own note ("Keep your VAT codes as you will be able to map them later")
 * means these are remapped at import, so an approximate code is a
 * starting point rather than a booked figure.
 *
 * A user-set `taxRate` on the receipt overrides all of this and is
 * emitted verbatim.
 *
 * Returns `Exempt` — the template's own vocabulary — only when the tax
 * is explicitly ZERO (parsed `0.00` line, or user-set rate 0). A missing
 * tax means the line FAILED TO PARSE, not that the receipt is exempt, so
 * it exports blank — the importer flags the row for human attention
 * instead of silently booking a false exemption. Blank likewise when the
 * numbers are unusable: no positive base, negative tax, or tax at or
 * above the base (no VAT regime does that; it means a mis-parse).
 */
export function taxCodeFor(r: Receipt, lang?: string): string {
  // A user-stated rate wins outright — they know what their accountant
  // needs, and no amount of division beats being told.
  if (r.taxRate !== undefined) {
    return r.taxRate === 0 ? 'Exempt' : `${r.taxRate}%`;
  }

  const tax = r.tax?.value;
  if (tax === undefined) return '';
  if (tax === 0) return 'Exempt';
  if (tax < 0) return '';

  // Prefer the printed subtotal; fall back to total − tax so receipts
  // that never printed a subtotal line still yield a rate.
  const base = r.subtotal?.value ?? r.total.value - tax;
  if (base <= 0) return '';
  if (tax >= base) return '';

  const pct = (tax / base) * 100;
  const candidates =
    (lang !== undefined ? VAT_RATES_BY_LANG[lang] : undefined) ?? ALL_VAT_RATES;
  const nearest = candidates.reduce((best, rate) =>
    Math.abs(rate - pct) < Math.abs(best - pct) ? rate : best,
  );
  if (Math.abs(nearest - pct) <= RATE_TOLERANCE) return `${nearest}%`;
  // Off every rate this country levies — emit what we actually measured
  // rather than snapping to a rate the receipt doesn't support.
  return `${pct.toFixed(1)}%`;
}

function receiptToRows(billNo: number, r: Receipt, lang?: string): string[][] {
  // Text fields go through escapeText (formula-injection guard); amount
  // columns stay raw — see csv-format.ts.
  const supplier = escapeText(r.vendor.value);
  const date = billDate(r.date.value);
  const account =
    r.category !== undefined ? escapeText(CATEGORY_EXPORT_LABELS[r.category]) : '';
  const taxCode = taxCodeFor(r, lang);
  const currency = escapeText(r.currency.value);
  const memo = escapeText(r.notes ?? '');

  // Terms / Location are optional and we hold nothing that maps to
  // them; DueDate repeats BillDate (see the module docstring).
  const base = [
    String(billNo),
    supplier,
    date,
    date,
    '',
    '',
    memo,
    account,
  ];

  // A receipt whose items didn't parse still has a trustworthy total —
  // emit it as a single line so the bill isn't silently dropped from
  // the import, matching export-csv.ts's behaviour.
  if (r.items.length === 0) {
    return [[...base, '', centsToDecimal(r.total.value), taxCode, '', currency]];
  }

  return r.items.map((item) => [
    ...base,
    escapeText(item.name.value),
    centsToDecimal(item.total.value),
    taxCode,
    '',
    currency,
  ]);
}

/**
 * Build the bill-import CSV for every successfully-parsed session.
 * `*BillNo` is a 1-based index over the exported bills — receipts carry
 * no invoice number of their own, and the importer only needs it to
 * group rows belonging to the same bill.
 */
export function exportBillCsvBatch(sessions: readonly ReceiptSession[]): Blob {
  const rows: string[][] = [Array.from(HEADERS)];
  let billNo = 0;
  for (const session of sessions) {
    if (session.state.kind !== 'success' || session.receipt === null) continue;
    billNo += 1;
    rows.push(...receiptToRows(billNo, session.receipt, session.lang));
  }
  // RFC 4180 line endings.
  const csv = rows.map((r) => r.map(quote).join(',')).join('\r\n');
  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}
